import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { QrCode, Clock, CalendarDays, Plus, Trash2, RefreshCw, Copy, ChevronLeft, ChevronRight, ChevronDown, AlertTriangle, Printer, Users, Download, Loader2, Check, History } from "lucide-react";
import { fmtDate } from "@/lib/format";

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function startOfWeek(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() - d.getDay()); // Sunday
  return isoDate(d);
}
// Start of the 7-day window aligned to the property's pay-period anchor weekday.
function alignedWeekStart(anchorIso, todayIso) {
  const anchor = new Date(anchorIso + "T00:00:00");
  const today = new Date(todayIso + "T00:00:00");
  if (isNaN(anchor.getTime())) return startOfWeek(todayIso);
  const idx = Math.floor((today - anchor) / 86400000 / 7);
  const s = new Date(anchor);
  s.setDate(anchor.getDate() + idx * 7);
  return isoDate(s);
}
function weekDates(startStr) {
  const out = [];
  const d = new Date(startStr + "T00:00:00");
  for (let i = 0; i < 7; i++) {
    out.push(isoDate(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
function timeOf(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit" }).format(d);
}
function fmtTime(iso, tz) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(d);
}
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function TimeClock() {
  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const { user } = useAuth();
  const canApprove = ["admin", "regional", "gm"].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(isoDate(new Date())));
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  // QR
  const [qrOpen, setQrOpen] = useState(false);
  const [kioskUrl, setKioskUrl] = useState(null);
  const [qrImg, setQrImg] = useState(null);

  // Time entries
  const [entries, setEntries] = useState([]);
  const [active, setActive] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", date: "", clock_in: "", clock_out: "" });
  // Schedule
  const [shifts, setShifts] = useState({}); // key `${eid}|${date}` -> {start,end,id}

  const days = useMemo(() => weekDates(weekStart), [weekStart]);
  const weekEnd = days[6];
  const todayIso = isoDate(new Date());
  const propTz = props.find((p) => p.id === propertyId)?.timezone || "America/New_York";

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    }).catch(() => {});
  }, []);

  // Align the 7-day window to the property's pay-period start weekday.
  useEffect(() => {
    const p = props.find((x) => x.id === propertyId);
    if (p && p.payroll_anchor) setWeekStart(alignedWeekStart(p.payroll_anchor, isoDate(new Date())));
  }, [propertyId, props]);

  useEffect(() => {
    if (!propertyId) return;
    api.get("/employees", { params: { property_id: propertyId } })
      .then((r) => setEmployees([...r.data].sort((a, b) =>
        (a.department || "").localeCompare(b.department || "") || (a.last_name || "").localeCompare(b.last_name || ""))))
      .catch(() => setEmployees([]));
  }, [propertyId]);

  const loadEntries = useCallback(() => {
    if (!propertyId) return;
    setLoading(true);
    api.get("/time-entries", { params: { property_id: propertyId, start: days[0], end: weekEnd } })
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [propertyId, days, weekEnd]);

  const loadShifts = useCallback(() => {
    if (!propertyId) return;
    api.get("/shifts", { params: { property_id: propertyId, start: days[0], end: weekEnd } })
      .then((r) => {
        const m = {};
        r.data.forEach((s) => { m[`${s.employee_id}|${s.date}`] = { start: s.start_time, end: s.end_time, id: s.id }; });
        setShifts(m);
      })
      .catch(() => setShifts({}));
  }, [propertyId, days, weekEnd]);

  useEffect(() => { loadEntries(); loadShifts(); }, [loadEntries, loadShifts]);

  // ----- live "on shift now" board (polls) -----
  const loadActive = useCallback(() => {
    if (!propertyId) return;
    api.get("/time-entries/active", { params: { property_id: propertyId } })
      .then((r) => setActive(r.data))
      .catch(() => setActive([]));
  }, [propertyId]);

  useEffect(() => {
    loadActive();
    const t = setInterval(loadActive, 20000);
    return () => clearInterval(t);
  }, [loadActive]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/time-entries/export", {
        params: { property_id: propertyId, start: days[0], end: weekEnd },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const name = (props.find((p) => p.id === propertyId)?.name || "Timesheet").replace(/\s+/g, "_");
      a.href = url;
      a.download = `${name}_Timesheet_${days[0]}_to_${weekEnd}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ }
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setExporting(false);
    }
  };

  // ----- weekly hours + OT -----
  const weeklyHours = useMemo(() => {
    const m = {};
    entries.forEach((e) => { m[e.employee_id] = (m[e.employee_id] || 0) + (e.flagged ? 0 : (e.hours || 0)); });
    return m;
  }, [entries]);

  const empName = (eid) => {
    const e = employees.find((x) => x.id === eid);
    return e ? `${e.first_name} ${e.last_name}` : (entries.find((x) => x.employee_id === eid)?.employee_name || "—");
  };

  // ----- QR -----
  const openQr = async () => {
    setQrOpen(true);
    setQrImg(null);
    const base = window.location.origin;
    try {
      const info = await api.get(`/properties/${propertyId}/kiosk-info`, { params: { base } });
      let url = info.data.url;
      if (!url) {
        const g = await api.post(`/properties/${propertyId}/kiosk-token`, null, { params: { base } });
        url = g.data.url;
      }
      setKioskUrl(url);
      const blob = await api.get(`/properties/${propertyId}/kiosk-qr`, { params: { base }, responseType: "blob" });
      setQrImg(URL.createObjectURL(blob.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const regenQr = async () => {
    const base = window.location.origin;
    try {
      const g = await api.post(`/properties/${propertyId}/kiosk-token`, null, { params: { base } });
      setKioskUrl(g.data.url);
      const blob = await api.get(`/properties/${propertyId}/kiosk-qr`, { params: { base }, responseType: "blob" });
      setQrImg(URL.createObjectURL(blob.data));
      toast.success("New kiosk code generated — old QR no longer works");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const printQr = () => {
    if (!qrImg) return;
    const w = window.open("", "_blank");
    const prop = props.find((p) => p.id === propertyId);
    w.document.write(`<html><head><title>Time Clock QR</title></head><body style="text-align:center;font-family:sans-serif;padding:40px">
      <h1>${prop?.name || "Property"}</h1>
      <h2>Employee Time Clock</h2>
      <img src="${qrImg}" style="width:340px;height:340px" />
      <p style="font-size:18px">Scan with your phone to clock in / out</p></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // ----- manual time entry -----
  const openAdd = (prefill = {}) => {
    setForm({ employee_id: prefill.employee_id || employees[0]?.id || "", date: prefill.date || days[0], clock_in: "09:00", clock_out: "17:00", break_minutes: 0 });
    setAddOpen(true);
  };
  const saveEntry = async () => {
    if (!form.employee_id) return toast.error("Pick an employee");
    try {
      await api.post("/time-entries", { property_id: propertyId, ...form });
      setAddOpen(false);
      loadEntries();
      loadActive();
      toast.success("Time entry added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const editEntry = async (id, field, value) => {
    try {
      await api.patch(`/time-entries/${id}`, { [field]: value });
      loadEntries();
      loadActive();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delEntry = async (id) => {
    try { await api.delete(`/time-entries/${id}`); loadEntries(); loadActive(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleApproveDay = async (e) => {
    try {
      await api.post("/timesheet/approve", { property_id: propertyId, entry_id: e.id, approved: !e.approved });
      loadEntries();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [approvingAll, setApprovingAll] = useState(false);
  const approveAllWeek = async (approved) => {
    setApprovingAll(true);
    try {
      const r = await api.post("/timesheet/approve", { property_id: propertyId, start: days[0], end: weekEnd, approved });
      toast.success(approved ? `Approved ${r.data.count} punch-day(s) for everyone this week` : "Unapproved this week");
      loadEntries();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setApprovingAll(false); }
  };
  const approveWholePeriod = async () => {
    setApprovingAll(true);
    try {
      const r = await api.post("/timesheet/approve-period", { property_id: propertyId, ref_date: days[0], approved: true });
      toast.success(`Approved the full pay period (${r.data.start} – ${r.data.end}) — ${r.data.count} punch-day(s)`);
      loadEntries();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setApprovingAll(false); }
  };
  const autoApproveClean = async () => {
    setApprovingAll(true);
    try {
      const r = await api.post("/timesheet/auto-approve", { property_id: propertyId, start: days[0], end: weekEnd });
      toast.success(`Auto-approved ${r.data.approved} clean day(s); ${r.data.skipped} left for review (edited / off-site / manual / no clock-out)`);
      loadEntries();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setApprovingAll(false); }
  };
  const approveDay = async (date, approved) => {
    setApprovingAll(true);
    try {
      const r = await api.post("/timesheet/approve", { property_id: propertyId, start: date, end: date, approved });
      toast.success(approved ? `Approved everyone for ${fmtDate(date)} — ${r.data.count} punch(es)` : `Unapproved ${fmtDate(date)}`);
      loadEntries();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setApprovingAll(false); }
  };
  const openAudit = async () => {
    setAuditOpen(true);
    try {
      const r = await api.get("/time-entries/audit", { params: { property_id: propertyId, start: days[0], end: weekEnd } });
      setAudit(r.data);
    } catch { setAudit([]); }
  };
  const allApproved = useMemo(() => entries.length > 0 && entries.every((e) => e.approved), [entries]);

  // ----- Week/Day view state -----
  const [viewMode, setViewMode] = useState("day");
  const [collapsedEmp, setCollapsedEmp] = useState({});
  const [dayDate, setDayDate] = useState(days[0]);
  // keep the selected day inside the visible week
  useEffect(() => { if (!days.includes(dayDate)) setDayDate(days.includes(todayIso) ? todayIso : days[0]); }, [weekStart]); // eslint-disable-line

  const deptOf = useCallback((e) => (e.department || (e.positions?.[0]) || "Unassigned"), []);
  const empSortKey = useCallback((name, dept) => `${(dept || "zzz").toLowerCase()}|${(name || "").toLowerCase()}`, []);
  const dayEntries = useMemo(() => entries.filter((e) => e.date === dayDate), [entries, dayDate]);
  const dayAllApproved = useMemo(() => dayEntries.length > 0 && dayEntries.every((e) => e.approved), [dayEntries]);

  // entries grouped by employee (sorted by dept, then name) for the Week view
  const empGroups = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      const g = map[e.employee_id] || (map[e.employee_id] = { employee_id: e.employee_id, name: e.employee_name, dept: deptOf(e), rows: [] });
      g.rows.push(e);
    });
    const arr = Object.values(map);
    arr.forEach((g) => g.rows.sort((a, b) => (a.date < b.date ? -1 : 1)));
    arr.sort((a, b) => empSortKey(a.name, a.dept).localeCompare(empSortKey(b.name, b.dept)));
    return arr;
  }, [entries, deptOf, empSortKey]);

  // every employee (sorted by dept, then name) with their punches for the selected day
  const dayRows = useMemo(() => {
    const byEmp = {};
    entries.filter((e) => e.date === dayDate).forEach((e) => { (byEmp[e.employee_id] = byEmp[e.employee_id] || []).push(e); });
    return [...employees]
      .map((emp) => ({ emp, name: `${emp.first_name} ${emp.last_name}`, dept: emp.department || (emp.positions?.[0]?.department) || "Unassigned", punches: byEmp[emp.id] || [] }))
      .sort((a, b) => empSortKey(a.name, a.dept).localeCompare(empSortKey(b.name, b.dept)));
  }, [entries, employees, dayDate, empSortKey]);

  const COLS = canApprove ? 11 : 10;
  const entryRow = (e, showEmp = true) => {
    const wk = weeklyHours[e.employee_id] || 0;
    const over = wk >= 40;
    const approaching = wk >= 32 && wk < 40;
    return (
      <tr key={e.id} className={`border-t border-border ${e.overnight ? "border-l-4 border-l-indigo-400 dark:border-l-indigo-500 " : ""}${e.approved ? "bg-green-100/70 dark:bg-green-500/15" : e.overnight ? "bg-indigo-50 dark:bg-indigo-500/10" : ""}`} data-testid={`tc-entry-${e.id}`}>
        <td className="p-3 font-medium">
          {showEmp ? e.employee_name : <span className="text-muted-foreground">↳</span>}
          {e.offsite && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400" data-testid={`tc-offsite-${e.id}`} title={e.clock_in_distance_m != null ? `${Math.round(e.clock_in_distance_m)} m from property` : "Location unavailable at clock-in"}>
              <AlertTriangle className="h-3 w-3" /> Off-site
            </span>
          )}
        </td>
        <td className="p-2">
          {(e.positions?.length || 0) > 1 ? (
            <Select value={e.department || ""} onValueChange={(v) => editEntry(e.id, "position", v)}>
              <SelectTrigger className="h-8 w-36" data-testid={`tc-dept-${e.id}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {e.positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-xs text-muted-foreground" data-testid={`tc-dept-${e.id}`}>{e.department || "—"}</span>
          )}
        </td>
        <td className="p-3">{fmtDate(e.date)}</td>
        <td className="p-2">
          <Input type="time" defaultValue={timeOf(e.clock_in, propTz)} onBlur={(ev) => ev.target.value !== timeOf(e.clock_in, propTz) && editEntry(e.id, "clock_in", ev.target.value)} className="h-8 w-28" data-testid={`tc-in-${e.id}`} />
        </td>
        <td className="p-2">
          <div className="flex items-center gap-1">
            <Input type="time" defaultValue={timeOf(e.clock_out, propTz)} onBlur={(ev) => ev.target.value !== timeOf(e.clock_out, propTz) && editEntry(e.id, "clock_out", ev.target.value)} className="h-8 w-28" data-testid={`tc-out-${e.id}`} />
            {e.overnight && (
              <span className="inline-flex items-center rounded-sm bg-purple-500/15 px-1 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-purple-600 dark:text-purple-400" data-testid={`tc-overnight-${e.id}`} title="Clock-out is on the next day (overnight / possible missed clock-out)">+1d</span>
            )}
          </div>
        </td>
        <td className="p-2">
          <Input type="number" min="0" defaultValue={e.break_minutes || 0} onBlur={(ev) => Number(ev.target.value || 0) !== (e.break_minutes || 0) && editEntry(e.id, "break_minutes", Number(ev.target.value || 0))} className="h-8 w-20" data-testid={`tc-break-${e.id}`} />
        </td>
        <td className="p-3 tabular-nums">
          {e.clock_out ? (
            e.flagged ? (
              <span className="inline-flex items-center gap-1 rounded-sm bg-red-500/15 px-1.5 py-0.5 text-[0.65rem] font-bold text-red-600 dark:text-red-400" data-testid={`tc-flagged-${e.id}`} title={`${e.hours.toFixed(2)} h is too long for one shift (likely a missed clock-out). Excluded from payroll until you fix the clock-out time.`}>
                <AlertTriangle className="h-3 w-3" /> {e.hours.toFixed(2)} · review
              </span>
            ) : (
              e.hours.toFixed(2)
            )
          ) : (
            <span className={e.on_break ? "text-blue-500" : "text-emerald-500"}>{e.on_break ? "on lunch" : "active"}</span>
          )}
        </td>
        <td className="p-3 tabular-nums">
          <span className={over ? "font-bold text-red-600 dark:text-red-500" : approaching ? "font-bold text-yellow-600 dark:text-yellow-500" : ""}>{wk.toFixed(2)}</span>
          {over && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-red-600 dark:text-red-500" title="40+ hrs — overtime" />}
          {approaching && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-yellow-600 dark:text-yellow-500" title="Approaching 40 hrs (32–39.99) — one shift left to adjust" />}
        </td>
        <td className="p-3 text-xs text-muted-foreground">{e.source}</td>
        {canApprove && (
          <td className="p-2">
            <Button variant={e.approved ? "default" : "outline"} size="sm"
              className={`h-8 ${e.approved ? "bg-green-600 hover:bg-green-700" : ""}`}
              onClick={() => toggleApproveDay(e)} data-testid={`tc-approve-${e.id}`}
              disabled={!e.approved && !e.clock_out}
              title={e.approved ? `Approved by ${e.approved_by || ""}` : !e.clock_out ? "Still clocked in — approve after clock-out" : "Approve this punch"}>
              <Check className="mr-1 h-3.5 w-3.5" /> {e.approved ? "Approved" : "Approve"}
            </Button>
          </td>
        )}
        <td className="p-2 text-right">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delEntry(e.id)} data-testid={`tc-del-${e.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
        </td>
      </tr>
    );
  };

  // ----- schedule -----
  const setShiftCell = (eid, date, part, value) => {
    setShifts((m) => ({ ...m, [`${eid}|${date}`]: { ...(m[`${eid}|${date}`] || {}), [part]: value } }));
  };
  const saveShift = async (eid, date) => {
    const cell = shifts[`${eid}|${date}`] || {};
    try {
      await api.post("/shifts", { property_id: propertyId, employee_id: eid, date, start_time: cell.start || "", end_time: cell.end || "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shiftWeek = (dir) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(isoDate(d));
  };

  return (
    <div className="space-y-6" data-testid="timeclock-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Time Clock</h1>
          <p className="text-sm text-muted-foreground">QR kiosk clock-in/out, timesheets & weekly schedule</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="label-caps text-[0.6rem] text-muted-foreground">Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="w-56" data-testid="tc-property-select"><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>
                {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openQr} disabled={!propertyId} data-testid="tc-qr-btn"><QrCode className="mr-2 h-4 w-4" /> Kiosk QR</Button>
        </div>
      </div>

      {/* Week nav */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="tc-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          {fmtDate(days[0])} – {fmtDate(weekEnd)}
        </div>
        <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="tc-week-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => {
          const p = props.find((x) => x.id === propertyId);
          setWeekStart(p?.payroll_anchor ? alignedWeekStart(p.payroll_anchor, isoDate(new Date())) : startOfWeek(isoDate(new Date())));
        }} data-testid="tc-week-today">This week</Button>
      </div>

      {/* On Shift Now */}
      <div className="rounded-lg border border-border bg-card p-4" data-testid="tc-onshift-panel">
        <div className="mb-3 flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {active.length > 0 && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${active.length > 0 ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
          </span>
          <span className="label-caps text-[0.65rem] text-muted-foreground">On Shift Now</span>
          <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-bold" data-testid="tc-onshift-count">{active.length}</span>
        </div>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="tc-onshift-empty">No one is clocked in right now.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {active.map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2" data-testid={`tc-onshift-${a.employee_id}`}>
                <Users className="h-4 w-4 text-emerald-500" />
                <div className="leading-tight">
                  <div className="text-sm font-semibold">{a.employee_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.department ? `${a.department} · ` : ""}in {fmtTime(a.clock_in, propTz)} · {a.elapsed_hours.toFixed(1)}h
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Tabs defaultValue="timesheet">
        <TabsList>
          <TabsTrigger value="timesheet" data-testid="tab-timesheet"><Clock className="mr-2 h-4 w-4" /> Time Entries</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule"><CalendarDays className="mr-2 h-4 w-4" /> Schedule</TabsTrigger>
        </TabsList>

        {/* ---------- Time Entries ---------- */}
        <TabsContent value="timesheet" className="mt-4 space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={openAudit} disabled={!propertyId} data-testid="tc-audit-btn">
              <History className="mr-2 h-4 w-4" /> Approved Changes
            </Button>
            <Button variant="outline" size="sm" onClick={exportXlsx} disabled={!propertyId || exporting || entries.length === 0} data-testid="tc-export-btn">
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export Week
            </Button>
            <Button variant="outline" size="sm" onClick={openAdd} disabled={!employees.length} data-testid="tc-add-entry-btn"><Plus className="mr-2 h-4 w-4" /> Add Entry</Button>
          </div>
          {canApprove && entries.length > 0 && (
            <div className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3" data-testid="tc-period-approvals">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground">Approve everyone for {fmtDate(days[0])} – {fmtDate(weekEnd)}:</span>
                {allApproved ? (
                  <Button size="sm" variant="outline" className="h-7 border-green-600 text-green-700 dark:text-green-400"
                    disabled={approvingAll} onClick={() => approveAllWeek(false)} data-testid="tc-unapprove-all-btn">
                    <Check className="mr-1 h-3.5 w-3.5" /> All approved — click to undo
                  </Button>
                ) : (
                  <Button size="sm" className="h-7 bg-green-600 text-white hover:bg-green-700"
                    disabled={approvingAll} onClick={() => approveAllWeek(true)} data-testid="tc-approve-all-btn">
                    {approvingAll ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Approve All This Week
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-7"
                  disabled={approvingAll} onClick={approveWholePeriod} data-testid="tc-approve-period-all-btn">
                  <CalendarDays className="mr-1 h-3.5 w-3.5" /> Approve Entire Pay Period
                </Button>
                <Button size="sm" variant="outline" className="h-7"
                  disabled={approvingAll} onClick={autoApproveClean} data-testid="tc-auto-approve-btn">
                  <Check className="mr-1 h-3.5 w-3.5" /> Auto-Approve Clean Punches
                </Button>
              </div>
            </div>
          )}
          {/* View toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="tc-view-toggle">
              <Button size="sm" variant={viewMode === "day" ? "default" : "ghost"} className="h-7" onClick={() => setViewMode("day")} data-testid="tc-view-day">Day</Button>
              <Button size="sm" variant={viewMode === "week" ? "default" : "ghost"} className="h-7" onClick={() => setViewMode("week")} data-testid="tc-view-week">Week</Button>
            </div>
            {viewMode === "day" && (
              <div className="flex flex-wrap gap-1" data-testid="tc-day-chips">
                {days.map((d) => (
                  <Button key={d} size="sm" variant={dayDate === d ? "default" : "outline"} className="h-8" onClick={() => setDayDate(d)} data-testid={`tc-day-chip-${d}`}>
                    {DOW[new Date(d + "T00:00:00").getDay()]} {d.slice(5)}
                  </Button>
                ))}
              </div>
            )}
            {viewMode === "day" && canApprove && dayEntries.length > 0 && (
              dayAllApproved ? (
                <Button size="sm" variant="outline" className="h-8 border-green-600 text-green-700 dark:text-green-400"
                  disabled={approvingAll} onClick={() => approveDay(dayDate, false)} data-testid="tc-approve-day-btn">
                  <Check className="mr-1 h-3.5 w-3.5" /> Day approved — click to undo
                </Button>
              ) : (
                <Button size="sm" className="h-8 bg-green-600 text-white hover:bg-green-700"
                  disabled={approvingAll} onClick={() => approveDay(dayDate, true)} data-testid="tc-approve-day-btn">
                  {approvingAll ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Approve This Day
                </Button>
              )
            )}
            <span className="text-xs text-muted-foreground">Sorted by department, then name</span>
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : viewMode === "day" ? (
            employees.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="tc-empty">
                No employees for this property yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border" data-testid="tc-day-table">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-left label-caps text-[0.6rem] text-muted-foreground">
                    <tr>
                      <th className="p-3">Employee</th><th className="p-3">Dept</th><th className="p-3">Date</th>
                      <th className="p-3">Clock In</th><th className="p-3">Clock Out</th>
                      <th className="p-3">Lunch (min)</th>
                      <th className="p-3">Hours</th><th className="p-3">Week Total</th>
                      <th className="p-3">Source</th>
                      {canApprove && <th className="p-3">Approved</th>}
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map(({ emp, name, dept, punches }) => (
                      punches.length > 0 ? (
                        <Fragment key={emp.id}>{punches.map((e) => entryRow(e))}</Fragment>
                      ) : (
                        <tr key={emp.id} className="border-t border-border opacity-60" data-testid={`tc-dayrow-empty-${emp.id}`}>
                          <td className="p-3 font-medium">{name}</td>
                          <td className="p-2 text-xs text-muted-foreground">{dept}</td>
                          <td className="p-3 text-xs italic text-muted-foreground" colSpan={COLS - 3}>No punch this day</td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="outline" className="h-8" onClick={() => openAdd({ employee_id: emp.id, date: dayDate })} data-testid={`tc-dayadd-${emp.id}`}><Plus className="mr-1 h-3.5 w-3.5" /> Add punch</Button>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : entries.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="tc-empty">
              No punches this week. Employees clock in by scanning the Kiosk QR, or add entries manually.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border" data-testid="tc-week-table">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-left label-caps text-[0.6rem] text-muted-foreground">
                  <tr>
                    <th className="p-3">Employee</th><th className="p-3">Dept</th><th className="p-3">Date</th>
                    <th className="p-3">Clock In</th><th className="p-3">Clock Out</th>
                    <th className="p-3">Lunch (min)</th>
                    <th className="p-3">Hours</th><th className="p-3">Week Total</th>
                    <th className="p-3">Source</th>
                    {canApprove && <th className="p-3">Approved</th>}
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {empGroups.map((g) => {
                    const collapsed = collapsedEmp[g.employee_id];
                    const gTotal = weeklyHours[g.employee_id] || 0;
                    const gApproved = g.rows.every((r) => r.approved);
                    return (
                      <Fragment key={g.employee_id}>
                        <tr className="cursor-pointer border-t border-border bg-secondary/60 hover:bg-secondary" onClick={() => setCollapsedEmp((c) => ({ ...c, [g.employee_id]: !c[g.employee_id] }))} data-testid={`tc-empgroup-${g.employee_id}`}>
                          <td className="p-3 font-bold" colSpan={2}>
                            <span className="inline-flex items-center gap-2">
                              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              {g.name}<span className="text-xs font-normal text-muted-foreground">· {g.dept}</span>
                            </span>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground" colSpan={COLS - 3}>
                            {g.rows.length} day(s) · {gTotal.toFixed(2)} hrs {gApproved && <span className="font-semibold text-green-600 dark:text-green-400">· all approved</span>}
                          </td>
                          <td></td>
                        </tr>
                        {!collapsed && g.rows.map((e) => entryRow(e, false))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Lunch minutes are unpaid and subtracted from the day's hours. Hours flow into Payroll Entry automatically (pre-fills "Hours Worked"). Weekly totals of 32–39.99 hrs are flagged in yellow (approaching the limit — about one shift left to adjust); 40+ hrs are flagged in red. Overnight shifts that cross midnight are tinted indigo with a <strong>+1d</strong> marker so night-audit hours are easy to verify.</p>
        </TabsContent>

        {/* ---------- Schedule ---------- */}
        <TabsContent value="schedule" className="mt-4">
          {employees.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No employees for this property yet.</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary label-caps text-[0.6rem] text-muted-foreground">
                  <tr>
                    <th className="sticky left-0 z-10 bg-secondary p-3 text-left">Employee</th>
                    {days.map((d) => (
                      <th key={d} className="p-2 text-center">{DOW[new Date(d + "T00:00:00").getDay()]}<div className="font-normal">{d.slice(5)}</div></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} className="border-t border-border" data-testid={`sched-row-${emp.id}`}>
                      <td className="sticky left-0 z-10 bg-card p-3 font-medium">{emp.first_name} {emp.last_name}<div className="text-xs text-muted-foreground">{emp.department}</div></td>
                      {days.map((d) => {
                        const cell = shifts[`${emp.id}|${d}`] || {};
                        return (
                          <td key={d} className="p-1 align-top">
                            <div className="flex flex-col gap-1">
                              <Input type="time" value={cell.start || ""} onChange={(ev) => setShiftCell(emp.id, d, "start", ev.target.value)} onBlur={() => saveShift(emp.id, d)} className="h-7 w-24 text-xs" data-testid={`sched-start-${emp.id}-${d}`} />
                              <Input type="time" value={cell.end || ""} onChange={(ev) => setShiftCell(emp.id, d, "end", ev.target.value)} onBlur={() => saveShift(emp.id, d)} className="h-7 w-24 text-xs" data-testid={`sched-end-${emp.id}-${d}`} />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Set start/end time per employee per day. Clear both times to remove a shift. Saves automatically.</p>
        </TabsContent>
      </Tabs>

      {/* Add entry dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="tc-add-dialog">
          <DialogHeader><DialogTitle>Add Time Entry</DialogTitle><DialogDescription>Manually record a punch (e.g. missed clock-in).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v, position: "" }))}>
                <SelectTrigger data-testid="tc-add-emp"><SelectValue /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(() => {
              const emp = employees.find((x) => x.id === form.employee_id);
              const depts = emp ? [emp.department, ...(emp.positions || []).map((p) => p.department)].filter((v, i, a) => v && a.indexOf(v) === i) : [];
              if (depts.length <= 1) return null;
              return (
                <div>
                  <Label>Department</Label>
                  <Select value={form.position || emp.department} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}>
                    <SelectTrigger data-testid="tc-add-dept"><SelectValue /></SelectTrigger>
                    <SelectContent>{depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              );
            })()}
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="tc-add-date" /></div>
              <div><Label>Clock In</Label><Input type="time" value={form.clock_in} onChange={(e) => setForm((f) => ({ ...f, clock_in: e.target.value }))} data-testid="tc-add-in" /></div>
              <div><Label>Clock Out</Label><Input type="time" value={form.clock_out} onChange={(e) => setForm((f) => ({ ...f, clock_out: e.target.value }))} data-testid="tc-add-out" /></div>
            </div>
            <div className="w-1/3">
              <Label>Lunch (min)</Label>
              <Input type="number" min="0" value={form.break_minutes ?? 0} onChange={(e) => setForm((f) => ({ ...f, break_minutes: Number(e.target.value || 0) }))} data-testid="tc-add-break" />
            </div>
          </div>
          <DialogFooter><Button onClick={saveEntry} data-testid="tc-add-save">Save Entry</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="tc-qr-dialog">
          <DialogHeader>
            <DialogTitle>Time Clock Kiosk QR</DialogTitle>
            <DialogDescription>Print this and post it at the front desk. Employees scan it with their phone to clock in/out — no PIN needed.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3">
            {qrImg ? <img src={qrImg} alt="Kiosk QR" className="h-56 w-56 rounded-lg border border-border bg-white p-2" data-testid="tc-qr-img" /> : <div className="flex h-56 w-56 items-center justify-center text-sm text-muted-foreground">Generating…</div>}
            {kioskUrl && (
              <div className="flex w-full items-center gap-2">
                <Input readOnly value={kioskUrl} className="text-xs" data-testid="tc-qr-url" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(kioskUrl); toast.success("Copied"); }} data-testid="tc-qr-copy"><Copy className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={printQr} disabled={!qrImg} data-testid="tc-qr-print"><Printer className="mr-2 h-4 w-4" /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approved Changes (audit) dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl" data-testid="tc-audit-dialog">
          <DialogHeader>
            <DialogTitle>Approved Changes — Time Sheet</DialogTitle>
            <DialogDescription>Every add, edit and delete to punches for {fmtDate(days[0])} – {fmtDate(weekEnd)}, with who made it and when.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {audit.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="tc-audit-empty">No changes recorded for this period.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary text-left label-caps text-[0.6rem] text-muted-foreground">
                  <tr><th className="p-2">When</th><th className="p-2">Employee</th><th className="p-2">Action</th><th className="p-2">Detail</th><th className="p-2">By</th></tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-border" data-testid={`tc-audit-${a.id}`}>
                      <td className="p-2 text-xs text-muted-foreground">{new Date(a.at).toLocaleString()}</td>
                      <td className="p-2">{a.employee_name}</td>
                      <td className="p-2">
                        <span className={`rounded px-1.5 py-0.5 text-[0.65rem] font-semibold ${a.action === "deleted" ? "bg-red-500/15 text-red-600 dark:text-red-400" : a.action === "added" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-blue-500/15 text-blue-600 dark:text-blue-400"}`}>{a.action}</span>
                      </td>
                      <td className="p-2 text-xs">{a.detail}</td>
                      <td className="p-2 text-xs text-muted-foreground">{a.user_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
